import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import config from "../../../config";
import TradingHeader from "../../../Pdf/PrintHeaderAccount";
import PrintFooter from "../../../Pdf/PrintFooter";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import { PrintFormateNumber, parseReportAmount } from "../../../Pdf/PrintFormateNumber";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import { Spin } from "antd";
import moment from "moment";

const COL = {
  drLabel: 170,
  drAmount: 113,
  crLabel: 170,
  crAmount: 114,
};

function buildTradingRows(trading, grossProfit) {
  const gp = parseReportAmount(grossProfit);
  const isProfit = gp > 0;
  const isLoss = gp < 0;

  const debitRows = [
    { label: "Opening stock (at cost)", amount: trading.OpeningStock },
    { label: "Purchases (period, at cost)", amount: trading.TotalPurchase },
  ];
  if (isProfit) {
    debitRows.push({ label: "Gross profit", amount: gp });
  }

  const creditRows = [
    { label: "Sales (period, revenue)", amount: trading.TotalSales },
    { label: "Closing stock (at cost)", amount: trading.ClosingStock },
  ];
  if (isLoss) {
    creditRows.push({ label: "Gross loss", amount: Math.abs(gp) });
  }

  const count = Math.max(debitRows.length, creditRows.length);
  const rows = [];
  for (let i = 0; i < count; i++) {
    rows.push({
      debit: debitRows[i],
      credit: creditRows[i],
    });
  }
  return rows;
}

function PrintTradingReport() {
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const urlParams = new URLSearchParams(window.location.search);
  const StartDate = urlParams.get("StartDate");
  const EndDate = urlParams.get("EndDate");

  const [tradingData, setTradingData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Print Trading Account";
    if (!StartDate || !EndDate || !UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Report/GetTradingAccountReport",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID: UserID,
        AccessKey: AccessKey,
        StartDate: moment(StartDate).isValid()
          ? moment(StartDate).format("YYYY-MM-DD")
          : StartDate,
        EndDate: moment(EndDate).isValid()
          ? moment(EndDate).format("YYYY-MM-DD")
          : EndDate,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setTradingData(response.data);
        }
        setLoading(false);
      })
      .catch(function (error) {
        console.log(error);
        setLoading(false);
      });
  }, [StartDate, EndDate, UserID, AccessKey]);

  const trading = tradingData.Trading || {};
  const tableRows = useMemo(
    () => buildTradingRows(trading, tradingData.GrossProfit),
    [trading, tradingData.GrossProfit]
  );

  const periodLabel = `${
    StartDate && moment(StartDate).isValid()
      ? moment(StartDate).format(config.date_format)
      : "—"
  } to ${
    EndDate && moment(EndDate).isValid()
      ? moment(EndDate).format(config.date_format)
      : "—"
  }`;

  const fmt = (val) => PrintFormateNumber(parseReportAmount(val), 0);

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <TradingHeader compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>Trading Account</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Period: {periodLabel}
            </Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Cost of goods sold: {fmt(trading.CostOfGoodsSold)} (Opening +
              Purchases − Closing)
            </Text>
          </View>

          <View style={saleReportStyles.table}>
            <View style={saleReportStyles.tableHead}>
              <View style={[saleReportStyles.th, { width: COL.drLabel }]}>
                <Text style={saleReportStyles.thValue}>Debit</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.drAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.crLabel }]}>
                <Text style={saleReportStyles.thValue}>Credit</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.crAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
            </View>

            {tableRows.map((row, index) => (
              <View key={index} style={saleReportStyles.row}>
                <View style={[saleReportStyles.td, { width: COL.drLabel }]}>
                  <Text style={saleReportStyles.tdValue}>
                    {row.debit?.label || ""}
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.drAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {row.debit ? fmt(row.debit.amount) : ""}
                  </Text>
                </View>
                <View style={[saleReportStyles.td, { width: COL.crLabel }]}>
                  <Text style={saleReportStyles.tdValue}>
                    {row.credit?.label || ""}
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.crAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {row.credit ? fmt(row.credit.amount) : ""}
                  </Text>
                </View>
              </View>
            ))}

            <View style={saleReportStyles.totalsRow}>
              <View style={[saleReportStyles.th, { width: COL.drLabel }]}>
                <Text style={saleReportStyles.thValue}>Total</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.drAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(tradingData.TotalAmountLeft)}
                </Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.crLabel }]}>
                <Text style={saleReportStyles.thValue}>Total</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.crAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(tradingData.TotalAmountRight)}
                </Text>
              </View>
            </View>
          </View>

          <View
            style={{ marginBottom: 20, marginTop: 0, top: 6, bottom: 6 }}
            fixed
          >
            <Text
              style={{ fontSize: "7pt", color: "#000" }}
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%" fileName="TradingAccount.pdf">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
}

export default PrintTradingReport;
